/**
 * Money handling for the fund.
 *
 * Every amount in this codebase is an integer number of **paise**, never a
 * float number of rupees. ₹1,00,000 split 12 ways is 8333.333... — in floats
 * that error compounds silently across a repayment schedule until the sum of
 * installments no longer equals the principal, which for an interest-free loan
 * is the one invariant that must never break.
 *
 * Rupees exist only at the edges: parsing what an admin types, and formatting
 * what a user reads.
 */

/** An integer number of paise. 100 paise = ₹1. */
export type Paise = number

/** Whole rupees — installments land on round rupee amounts by default. */
export const RUPEE = 100

export function isValidPaise(value: unknown): value is Paise {
  return typeof value === 'number' && Number.isSafeInteger(value)
}

/** Asserts an amount is a usable integer paise value, throwing if not. */
export function assertPaise(value: unknown, label = 'amount'): Paise {
  if (!isValidPaise(value)) {
    throw new TypeError(`${label} must be an integer number of paise, got ${String(value)}`)
  }
  return value
}

export function rupeesToPaise(rupees: number): Paise {
  if (!Number.isFinite(rupees)) {
    throw new TypeError(`Cannot convert non-finite rupees: ${rupees}`)
  }
  // Scale before rounding so 1234.565 doesn't fall foul of binary representation.
  return Math.round(rupees * 100)
}

export function paiseToRupees(paise: Paise): number {
  return assertPaise(paise) / 100
}

/**
 * Parses free-typed rupee input — tolerates "₹", commas (including Indian
 * grouping), and surrounding spaces. Returns null for anything unparseable so
 * callers can show a field error rather than storing NaN.
 */
export function parseRupeeInput(input: string): Paise | null {
  const cleaned = input.replace(/[₹,\s]/g, '')
  if (cleaned === '' || !/^-?\d*\.?\d*$/.test(cleaned)) return null
  const rupees = Number(cleaned)
  if (!Number.isFinite(rupees)) return null
  return rupeesToPaise(rupees)
}

const inrWhole = new Intl.NumberFormat('en-IN', {
  style: 'currency',
  currency: 'INR',
  minimumFractionDigits: 0,
  maximumFractionDigits: 0,
})

const inrPaise = new Intl.NumberFormat('en-IN', {
  style: 'currency',
  currency: 'INR',
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
})

/**
 * Formats paise as INR with Indian digit grouping (₹1,00,000).
 *
 * Paise are hidden when the amount is a whole rupee, because in day-to-day use
 * every amount is round and "₹8,333.00" is just noise. When paise are present
 * they are always shown — a rounded display of a non-round amount is how
 * reconciliation disputes start.
 */
export function formatINR(paise: Paise, opts?: { alwaysShowPaise?: boolean }): string {
  assertPaise(paise, 'formatINR amount')
  const showPaise = opts?.alwaysShowPaise === true || paise % 100 !== 0
  const fmt = showPaise ? inrPaise : inrWhole
  return fmt.format(paise / 100)
}

/** Compact form for dense list views: ₹1.2L, ₹45.0K, ₹1.5Cr. */
export function formatINRCompact(paise: Paise): string {
  assertPaise(paise, 'formatINRCompact amount')
  const rupees = Math.abs(paise) / 100
  const sign = paise < 0 ? '-' : ''
  if (rupees >= 1_00_00_000) return `${sign}₹${(rupees / 1_00_00_000).toFixed(1)}Cr`
  if (rupees >= 1_00_000) return `${sign}₹${(rupees / 1_00_000).toFixed(1)}L`
  if (rupees >= 1_000) return `${sign}₹${(rupees / 1_000).toFixed(1)}K`
  return formatINR(paise)
}

/** Indian lakh wording for public highlights — e.g. ₹8 lakhs, ₹8.5 lakhs. */
export function formatINRLakh(paise: Paise): string {
  assertPaise(paise, 'formatINRLakh amount')
  const rupees = Math.abs(paise) / 100
  const sign = paise < 0 ? '-' : ''
  if (rupees < 1_00_000) return formatINR(paise)
  const lakhs = rupees / 1_00_000
  const amount =
    lakhs % 1 === 0 ? String(lakhs) : lakhs.toFixed(1).replace(/\.0$/, '')
  return `${sign}₹${amount} ${lakhs === 1 ? 'lakh' : 'lakhs'}`
}

/**
 * Splits a total into `parts`, rounding each part down to `roundingUnit` and
 * giving the **remainder to the final part**, so the parts always sum back to
 * exactly the total.
 *
 * Rounding to whole rupees by default matters practically: this fund collects
 * repayments in cash, and "₹8,333.33 every month" is not a thing anyone can
 * hand over. ₹100000 over 12 becomes 11 × ₹8,333 and a final ₹8,337.
 *
 * The final part absorbing the remainder is deliberate — a borrower should
 * never find their *last* payment is smaller than agreed and assume they are
 * done early.
 */
export function splitAmount(total: Paise, parts: number, roundingUnit: Paise = RUPEE): Paise[] {
  assertPaise(total, 'split total')
  if (!Number.isInteger(parts) || parts < 1) {
    throw new RangeError(`parts must be a positive integer, got ${parts}`)
  }
  if (total < 0) throw new RangeError('Cannot split a negative amount')
  if (!Number.isInteger(roundingUnit) || roundingUnit < 1) {
    throw new RangeError(`roundingUnit must be a positive integer, got ${roundingUnit}`)
  }
  if (total === 0) return Array.from({ length: parts }, () => 0)

  const base = Math.floor(total / parts / roundingUnit) * roundingUnit

  // Too small to round: fall back to plain paise so no part is zero while the
  // last one swallows everything. Splitting ₹5 into 12 genuinely cannot land on
  // whole rupees.
  if (base === 0) {
    const flat = Math.floor(total / parts)
    const result = Array.from({ length: parts }, () => flat)
    result[parts - 1] = total - flat * (parts - 1)
    return result
  }

  const result = Array.from({ length: parts }, () => base)
  result[parts - 1] = total - base * (parts - 1)
  return result
}

/** Sums paise amounts, staying in integer space. */
export function sumPaise(amounts: readonly Paise[]): Paise {
  return amounts.reduce<Paise>((acc, a) => acc + assertPaise(a), 0)
}

/** Percentage of `part` against `whole`, rounded to `dp`. Returns 0 for a zero whole. */
export function percentOf(part: Paise, whole: Paise, dp = 2): number {
  if (whole === 0) return 0
  const factor = 10 ** dp
  return Math.round((part / whole) * 100 * factor) / factor
}
